<script setup>
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import { ref, computed,onUnmounted } from 'vue';
import { useStore } from 'vuex'
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import axios from 'axios';
import { authHeader } from '@/helpers/authservice/auth-header';

dayjs.extend(localizedFormat)

onUnmounted(() => {
  order_of_payment.value = null;
  order_of_payment_search_input.value = '';
})

const title = ref('Order of Payment');
const items = ref([
  {
    text: "Home",
  },
    {
    text: "Payment",
  },
  {
    text: "Order of Payment",
    active: true,
  },
]);

const store = useStore();
const loading = computed(() => store.state.globals.loading);
const notification = computed(() => store ? store.state.notification : null);

const order_of_payment = computed({
  get:() => store.state.payment.order_of_payment,
  set:(val) => store.commit('payment/SET_ORDER_OF_PAYMENT',val)
});
const order_of_payment_search_input = computed({
  get:() => store.state.payment.order_of_payment_search_input,
  set:(val) => store.commit('payment/SET_ORDER_OF_PAYMENT_SEARCH_INPUT',val)
});

const getOrderOfPayment = () => store.dispatch('payment/getOrderOfPayment');
store.dispatch('notification/clear', null, { root: true });

const search = async () =>{
  order_of_payment.value = null;
  await getOrderOfPayment();
  order_of_payment_search_input.value = '';
  if(order_of_payment.value){
      await getOrderOfPaymentPdf(order_of_payment.value.id)
  }
}

const viewNov = async (text) =>{
  order_of_payment_search_input.value = text;
  await search();
}

const pdf_url = ref(null);

const getOrderOfPaymentPdf = async (traffic_record_id) => {
    const {data} = await axios.get(`payment/official_receipt/${traffic_record_id}/preview-pdf`, {
      responseType: "arraybuffer",
      headers: { ...authHeader() }
    });
    const blob = new Blob([data], { type: 'application/pdf' });
    const objectUrl = URL.createObjectURL(blob);
    pdf_url.value = objectUrl;
}

</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <b-alert v-if="notification && notification.message"
              show :variant="notification.type" class="fade show" role="alert">
              {{ notification.message }}
          </b-alert>
          <b-overlay :show="loading">
            <div class="card">
              <div class="card-header">
                  <div class="form-group">
                    <label for="search">Barcode the nov or type the nov # and press enter</label>
                    <input type="text" class="form-control" id="search" autofocus v-model="order_of_payment_search_input" @keyup.enter="search">
                  </div>
              </div>
            </div>
          </b-overlay>
        </div>
      </div>
      <div class="row" v-if="order_of_payment">
        <div class="col-5">
          <b-overlay :show="loading">
            <div class="card">
              <div class="card-header">
                <table class="table table-bordered table-sm m-0">
                  <tbody>
                    <tr>
                      <th colspan="2" class="text-center">{{order_of_payment.notice_of_violation_number ? order_of_payment.notice_of_violation_number : ''}}</th>
                    </tr>
                    <tr>
                      <td style="width:230px;" class="no-wrap">NOV</td>
                      <td >
                        <p class="m-0 p-0" v-for="(entry, index) in order_of_payment.violation_penalties" :key="index"><a target="_blank" :href="entry.generated_pdf_file_link">{{order_of_payment.notice_of_violation_number ? (index+1) + " - " + order_of_payment.notice_of_violation_number : ''}}</a></p>
                        <p class="m-0 py-2"><small>Click to view nov(pdf)</small></p>
                        </td>
                    </tr>
                    <tr>
                      <td class="no-wrap">OVR</td>
                      <td>{{order_of_payment.ovr ? order_of_payment.ovr.ovr_number : ''}}</td>
                    </tr>
                    <tr>
                      <td class="no-wrap">File Name</td>
                      <td>{{order_of_payment.filename ? order_of_payment.filename : ''}}</td>
                    </tr>
                    <tr>
                      <td class="no-wrap">Violation Date</td>
                      <td>{{order_of_payment.timestamp ? dayjs(order_of_payment.timestamp).format('LL') : ''}}</td>
                    </tr>
                    <tr>
                      <td class="no-wrap">Violation Time</td>
                      <td>{{order_of_payment.timestamp ? dayjs(order_of_payment.timestamp).format('LTS') : ''}}</td>
                    </tr>
                    <tr>
                      <td class="no-wrap">Violation Location</td>
                      <td>{{order_of_payment.violation_information.location ? order_of_payment.violation_information.location : ''}}</td>
                    </tr>
                    <tr>
                      <td colspan="2">
                        <div class="text-center">
                          Violations
                        </div> 
                        <table class="table table-bordered table-sm m-0">
                          <tbody v-for="(entry, index) in order_of_payment.violations" :key="index">
                            <tr>
                              <td>
                                {{entry.name ? entry.name : ''}}
                              </td>
                              <td style="width:80px;">
                                {{entry.penalty_fee ? entry.penalty_fee : 0}}
                              </td>
                            </tr>
                          </tbody>
                          <tfoot>
                            <tr>
                                <td>
                                  <b>Total</b>
                                </td>
                                <td>
                                  <b>{{order_of_payment.total_penalty_fee}}</b>
                                </td>
                            </tr>
                          </tfoot>
                        </table>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="2">
                        <div class="text-center">
                          MV Record
                        </div> 
                        <table class="table table-bordered table-sm m-0">
                          <tbody>
                            <tr>
                              <td style="width:180px;">Plate #</td>
                              <td>{{order_of_payment.lto_record.plate_number ? order_of_payment.lto_record.plate_number : ''}}</td>
                            </tr>
                            <tr>
                              <td>MV File #</td>
                              <td>{{order_of_payment.lto_record.mv_file_number ? order_of_payment.lto_record.mv_file_number : ''}}</td>
                            </tr>
                            <tr>
                              <td>Series</td>
                              <td>{{order_of_payment.lto_record.series ? order_of_payment.lto_record.series : ''}}</td>
                            </tr>
                            <tr>
                              <td>Year Model</td>
                              <td>{{order_of_payment.lto_record.year_model ? order_of_payment.lto_record.year_model : ''}}</td>
                            </tr>
                            <tr>
                              <td>Classification</td>
                              <td>{{order_of_payment.lto_record.classification ? order_of_payment.lto_record.classification : ''}}</td>
                            </tr>
                            <tr>
                              <td>Body Type</td>
                              <td>{{order_of_payment.lto_record.body_type ? order_of_payment.lto_record.body_type : ''}}</td>
                            </tr>
                            <tr>
                              <td>Owner Name</td>
                              <td>{{order_of_payment.lto_record.owner_name ? order_of_payment.lto_record.owner_name : ''}}</td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                    <tr>
                      <td>Payment Status</td>
                      <td v-if="order_of_payment.is_paid == 1">
                        <span class="text-success">Paid</span> - {{dayjs(order_of_payment.payment_date).format('LLL')}}
                      </td>
                      <td v-else-if="order_of_payment.is_voided == 1">
                        <span class="text-danger">Voided</span> - {{dayjs(order_of_payment.payment_date).format('LLL')}}
                      </td>
                      <td v-else>
                        <span class="text-danger">Unpaid</span>
                      </td>
                    </tr>
                    <tr>
                      <td v-if="order_of_payment.other_novs_with_same_vehicle_file_number.length" colspan="2">
                        <div class="text-center">
                          Other NOV with same MV File #
                        </div> 
                        <table class="table table-bordered table-sm m-0">
                          <tbody v-for="(item) in order_of_payment.other_novs_with_same_vehicle_file_number" :key="item.notice_of_violation_number">
                            <tr>
                              <td>
                                <a href="#" @click.prevent="viewNov(item.notice_of_violation_number)">{{ item.notice_of_violation_number }}</a><br/>
                                <p><small>(Click to View Order of payment for this other Nov)</small></p>
                              </td>
                              <td style="width:80px;">
                                <span v-if="item.is_voided == 1">Voided </span>
                                <span v-else-if="item.is_paid == true" class="text-success">Paid</span>
                                <span v-else class="text-danger">Unpaid</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
                
              </div>
            </div>
          </b-overlay>
        </div>
        <div class="col-7">
          <b-overlay :show="loading">
            <div class="card">
              <div class="card-header">
                <template v-if="order_of_payment.is_voided == 1">
                    <h1 class="text-center" style="font-weight: bold;"> --VOIDED-- </h1>
                </template>
                <template v-else-if="order_of_payment.number">
                    <h1 class="text-success text-center" style="font-weight: bold;"> --PAID-- </h1>
                </template>
                <template v-else>
                    <iframe type="application/pdf" :src="pdf_url" style="width: 100%;height: 800px;"></iframe>
                </template>
              </div>
            </div>
          </b-overlay>
        </div>
      </div>
    </div>
  </Layout>
</template>
